/**
 * AI Moods Management — Redesigned with Drag-Drop Grid
 * Two-panel card grid layout mirroring AI Styles page.
 * Teal branded header, 2-column card grid (left), App Mood Grid panel (right).
 * Edit/Create modal preserved. Analytics tab preserved.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tab,
  Tabs,
  TextField,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Mood as MoodIcon,
  Assessment as AnalyticsIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  DndContext,
  DragOverlay,
} from '@dnd-kit/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import { useForm, Controller } from 'react-hook-form';
import AdminEditDialogShell from '../shared/AdminEditDialogShell';
import PromptField from '../shared/PromptField';
import UnsavedChangesBar from './UnsavedChangesBar';
import { colors } from '../../theme/designTokens';

// Moods submodules
import { AIMood, MoodOrderItem } from './moods/types';
import { MAX_ACTIVE_MOODS } from './moods/constants';
import { useMoodsData } from './moods/useMoodsData';
import { useMoodDragReorder } from './moods/useMoodDragReorder';
import MoodCardGrid from './moods/MoodCardGrid';
import MoodDragOverlay from './moods/MoodDragOverlay';
import AppMoodGridPanel from './moods/AppMoodGridPanel';

// --- Tab Panel ---
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`moods-tabpanel-${index}`}
      aria-labelledby={`moods-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export const AIMoodsManagement: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMood, setEditingMood] = useState<AIMood | null>(null);

  // Data hook
  const {
    moods,
    analytics,
    loading,
    handleRefresh,
    handleToggleStatus,
    handleDeleteMood,
    handleSubmitMood,
  } = useMoodsData();

  // Map moods to order items for drag hook
  const moodOrderItems: MoodOrderItem[] = useMemo(
    () => moods.map(m => ({ id: m.id, name: m.display_name, isActive: m.is_active })),
    [moods],
  );

  // DnD hook
  const {
    hasUnsavedOrder,
    savingOrder,
    activeDragName,
    sensors,
    customCollisionDetection,
    getOrderedItems,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    handleSaveOrder,
    handleDiscardOrder,
  } = useMoodDragReorder(moodOrderItems);

  // Form for edit/create modal
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      mood_key: '',
      display_name: '',
      description: '',
      emotional_keywords: '',
      color_primary: '#6B8CAE',
      color_secondary: '#E6F0FF',
      icon_name: 'mood',
      ai_prompt_template: '',
      is_active: true,
    },
  });

  const handleCreateMood = () => {
    reset({
      mood_key: '',
      display_name: '',
      description: '',
      emotional_keywords: '',
      color_primary: '#6B8CAE',
      color_secondary: '#E6F0FF',
      icon_name: 'mood',
      ai_prompt_template: '',
      is_active: true,
    });
    setEditingMood(null);
    setIsEditModalOpen(true);
  };

  const handleEditMood = (mood: AIMood) => {
    reset({
      mood_key: mood.mood_key,
      display_name: mood.display_name,
      description: mood.description,
      emotional_keywords: Array.isArray(mood.emotional_keywords)
        ? mood.emotional_keywords.join(', ')
        : '',
      color_primary: mood.color_primary,
      color_secondary: mood.color_secondary,
      icon_name: mood.icon_name,
      ai_prompt_template: mood.grok_prompt,
      is_active: mood.is_active,
    });
    setEditingMood(mood);
    setIsEditModalOpen(true);
  };

  const onSubmit = async (data: any) => {
    // Convert comma-separated keywords to array
    const submitData = {
      ...data,
      emotional_keywords: data.emotional_keywords
        ? data.emotional_keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
        : [],
    };
    await handleSubmitMood(submitData, editingMood, () => setIsEditModalOpen(false));
  };

  // Computed stats
  const orderedItems = getOrderedItems();
  const activeOrdered = orderedItems.filter(i => i.isActive);
  const activeCount = moods.filter(m => m.is_active).length;
  const inGridCount = Math.min(activeCount, MAX_ACTIVE_MOODS);
  const totalCount = moods.length;
  const inactiveCount = totalCount - activeCount;

  const panelStats = {
    inGrid: inGridCount,
    total: totalCount,
    inactive: inactiveCount,
    totalUsage: analytics?.totalUsages || 0,
  };

  // Analytics chart data generators
  const generateEmotionalDistribution = () => {
    const emotions = ['Joy', 'Peace', 'Energy', 'Reflection', 'Hope', 'Warmth'];
    return emotions.map(emotion => ({
      emotion,
      value: Math.floor(Math.random() * 100) + 20,
    }));
  };

  const generateUsageOverTime = () => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return {
        date: date.toISOString().split('T')[0],
        usage: Math.floor(Math.random() * 200) + 100,
      };
    }).reverse();
  };

  const generateStatusData = () => [
    { name: 'Active', count: activeCount },
    { name: 'Inactive', count: inactiveCount },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#EDE5D6' }}>
      {/* ===== Branded Teal Header ===== */}
      <Box
        sx={{
          bgcolor: colors.deepTeal[500],
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '12px 12px 0 0',
          px: 3.5,
          pt: 3,
          pb: 2.5,
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Dot texture overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.06,
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '18px 18px',
            pointerEvents: 'none',
          }}
        />

        {/* Left column */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {/* Breadcrumb */}
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 500,
              fontSize: '0.72rem',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '0.04em',
              mb: 1.5,
            }}
          >
            Admin Dashboard / AI & Styles / AI Moods
          </Typography>

          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.3rem',
              }}
            >
              😊
            </Box>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 400,
                fontSize: '1.625rem',
                color: '#fff',
                letterSpacing: '-0.01em',
              }}
            >
              AI Moods
            </Typography>
          </Box>

          {/* Subtitle */}
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.55)',
              mb: 2.5,
            }}
          >
            Manage emotional moods for{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
              Memory Artisan&trade;
            </Box>
            {' '}chapter creation &middot; Position <strong>#1</strong> = &ldquo;Most Popular&rdquo; badge only
            &mdash; new chapters always default to the Neutral mood (no emotional overlay); members change theirs in app Settings
          </Typography>

          {/* Stats chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`${activeCount} Active`}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 600,
                fontSize: '0.72rem',
                bgcolor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                '& .MuiChip-label': { px: 1.2 },
              }}
              icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#4ade80', ml: 0.8 }} />}
            />
            <Chip
              size="small"
              label={`${inGridCount} In Grid`}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 600,
                fontSize: '0.72rem',
                bgcolor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                '& .MuiChip-label': { px: 1.2 },
              }}
              icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colors.warmGold[500], ml: 0.8 }} />}
            />
            <Chip
              size="small"
              label={`${totalCount} Total`}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 600,
                fontSize: '0.72rem',
                bgcolor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                '& .MuiChip-label': { px: 1.2 },
              }}
            />
          </Box>
        </Box>

        {/* Right column: action buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1, position: 'relative', ml: 3 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreateMood}
            sx={{
              bgcolor: colors.warmGold[500],
              color: '#fff',
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.78rem',
              textTransform: 'none',
              borderRadius: '8px',
              px: 2,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: colors.warmGold[600] },
            }}
          >
            Add New Mood
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.25)',
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.78rem',
              textTransform: 'none',
              borderRadius: '8px',
              px: 2,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ===== Tab Bar ===== */}
      <Box
        sx={{
          bgcolor: colors.deepTeal[700],
          px: 2,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.warmGold[500],
              height: 3,
            },
          }}
        >
          <Tab
            icon={<MoodIcon sx={{ fontSize: 18 }} />}
            label={`Moods (${totalCount})`}
            iconPosition="start"
            sx={{
              minHeight: 48,
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              color: 'rgba(255,255,255,0.55)',
              '&.Mui-selected': { color: '#fff' },
            }}
          />
          <Tab
            icon={<AnalyticsIcon sx={{ fontSize: 18 }} />}
            label="Analytics"
            iconPosition="start"
            sx={{
              minHeight: 48,
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              color: 'rgba(255,255,255,0.55)',
              '&.Mui-selected': { color: '#fff' },
            }}
          />
        </Tabs>
      </Box>

      {/* ===== Tab 0: Moods Grid ===== */}
      <TabPanel value={tabValue} index={0}>
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragCancel={handleDragCancel}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 290px' },
              minHeight: 'calc(100vh - 280px)',
            }}
          >
            {/* Left panel: Card grid */}
            <MoodCardGrid
              orderedItems={orderedItems}
              allMoods={moods}
              loading={loading}
              onToggle={handleToggleStatus}
              onEdit={handleEditMood}
              onDelete={(id) => handleDeleteMood(id)}
            />

            {/* Right panel: App Mood Grid */}
            <AppMoodGridPanel
              activeItems={activeOrdered}
              stats={panelStats}
            />
          </Box>

          <DragOverlay dropAnimation={null}>
            <MoodDragOverlay name={activeDragName} />
          </DragOverlay>
        </DndContext>

        {/* Unsaved changes bar */}
        <UnsavedChangesBar
          visible={hasUnsavedOrder}
          saving={savingOrder}
          onSave={handleSaveOrder}
          onDiscard={handleDiscardOrder}
        />
      </TabPanel>

      {/* ===== Tab 1: Analytics ===== */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Summary Stats */}
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Total Moods</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        {analytics?.totalMoods || 0}
                      </Typography>
                    </Box>
                    <MoodIcon sx={{ color: theme.palette.primary.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Active Moods</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                        {activeCount}
                      </Typography>
                    </Box>
                    <MoodIcon sx={{ color: theme.palette.success.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Total Usages</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                        {analytics?.totalUsages || 0}
                      </Typography>
                    </Box>
                    <AnalyticsIcon sx={{ color: theme.palette.warning.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2">Users w/ Prefs</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                        {analytics?.totalUsersWithPreferences || 0}
                      </Typography>
                    </Box>
                    <MoodIcon sx={{ color: theme.palette.secondary.main, opacity: 0.7, fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Mood Status</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={generateStatusData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {generateStatusData().map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Usage Over Time</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={generateUsageOverTime()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="usage" fill={theme.palette.secondary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Emotional Distribution</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={generateEmotionalDistribution()}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="emotion" />
                      <PolarRadiusAxis />
                      <Radar
                        name="Emotional Intensity"
                        dataKey="value"
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.main}
                        fillOpacity={0.6}
                      />
                      <RechartsTooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      {/* ===== Edit/Create Mood Modal ===== */}
      <AdminEditDialogShell
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        accentColor="#D4AC0D"
        typeInitial="M"
        title={`${editingMood ? 'Edit' : 'Create'} mood — ${watch('display_name') || 'Untitled'}`}
        subtitle="AI mood overlay · injected into every chapter transformation"
        formId="mood-edit-form"
        isDirty={isDirty}
        showActiveToggle
        isActive={watch('is_active')}
        onToggleActive={() => {
          const current = watch('is_active');
          reset({ ...watch(), is_active: !current }, { keepDirty: true });
        }}
      >
        <form id="mood-edit-form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="mood_key"
                  control={control}
                  rules={{ required: 'Mood key is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Mood Key"
                      fullWidth
                      error={!!errors.mood_key}
                      helperText={errors.mood_key?.message as string}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="display_name"
                  control={control}
                  rules={{ required: 'Display name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Display Name"
                      fullWidth
                      error={!!errors.display_name}
                      helperText={errors.display_name?.message as string}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: 'Description is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message as string}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="emotional_keywords"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Emotional Keywords (comma-separated)"
                      fullWidth
                      helperText="e.g. warmth, tenderness, yearning, bittersweet"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="ai_prompt_template"
                  control={control}
                  rules={{ required: 'Grok prompt is required' }}
                  render={({ field }) => (
                    <PromptField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      inputRef={field.ref}
                      label="GROK AI PROMPT"
                      helperText="The AI prompt template that will be injected for this mood"
                      accentColor="#D4AC0D"
                      minRows={10}
                      error={!!errors.ai_prompt_template}
                      errorMessage={errors.ai_prompt_template?.message as string}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="color_primary"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Primary Color" fullWidth placeholder="#6B8CAE" />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="color_secondary"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Secondary Color" fullWidth placeholder="#E6F0FF" />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="icon_name"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Icon Name" fullWidth placeholder="mood" />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
      </AdminEditDialogShell>
    </Box>
  );
};

export default AIMoodsManagement;
